import React, { useEffect, useRef } from 'react';

interface ParticleEffectsProps {
  theme: 'default' | 'green' | 'bubbles' | 'raindrops' | 'snow' | 'floating';
  isActive: boolean;
}

const ParticleEffects: React.FC<ParticleEffectsProps> = ({ theme, isActive }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Debug log to verify state changes
    console.log(`ParticleEffects: isActive=${isActive}, theme=${theme}`);

    // Clear any existing animation
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }

    // If particles are not active, clear the canvas and return
    if (!isActive) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      console.log('ParticleEffects: Particles disabled, canvas cleared');
      return;
    }

    // Set canvas dimensions
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles: any[] = [];
    const particleCount = theme === 'snow' ? 100 : theme === 'raindrops' ? 50 : 30;

    // Particle class
    class Particle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      size: number;
      opacity: number;
      color: string;
      type: string;

      constructor() {
        this.x = Math.random() * (canvas?.width || window.innerWidth);
        this.y = Math.random() * (canvas?.height || window.innerHeight);
        this.vx = (Math.random() - 0.5) * 0.5;
        this.vy = Math.random() * 0.5 + 0.5;
        this.size = Math.random() * 3 + 1;
        this.opacity = Math.random() * 0.5 + 0.3;
        this.type = theme;
        
        // Set colors based on theme
        switch (theme) {
          case 'green':
            this.color = `hsl(${120 + Math.random() * 60}, 70%, 60%)`;
            break;
          case 'bubbles':
            this.color = `hsla(${180 + Math.random() * 60}, 80%, 70%, ${this.opacity})`;
            break;
          case 'raindrops':
            this.color = `hsla(210, 80%, 70%, ${this.opacity})`;
            break;
          case 'snow':
            this.color = `hsla(0, 0%, 100%, ${this.opacity})`;
            break;
          case 'floating':
            this.color = `hsla(${Math.random() * 360}, 70%, 70%, ${this.opacity})`;
            break;
          default:
            this.color = `hsla(${200 + Math.random() * 60}, 70%, 70%, ${this.opacity})`;
        }
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;

        // Bounce off edges for bubbles and floating particles
        if (theme === 'bubbles' || theme === 'floating') {
          if (this.x < 0 || this.x > (canvas?.width || window.innerWidth)) this.vx *= -1;
          if (this.y < 0 || this.y > (canvas?.height || window.innerHeight)) this.vy *= -1;
        }

        // Reset particles that go off screen
        if (this.y > (canvas?.height || window.innerHeight) + 10) {
          this.y = -10;
          this.x = Math.random() * (canvas?.width || window.innerWidth);
        }

        if (this.x > (canvas?.width || window.innerWidth) + 10) {
          this.x = -10;
          this.y = Math.random() * (canvas?.height || window.innerHeight);
        }
      }

      draw() {
        if (!ctx) return;
        
        ctx.save();
        ctx.globalAlpha = this.opacity;

        switch (theme) {
          case 'bubbles':
            // Draw bubble with gradient
            const gradient = ctx.createRadialGradient(
              this.x, this.y, 0,
              this.x, this.y, this.size
            );
            gradient.addColorStop(0, 'rgba(255, 255, 255, 0.8)');
            gradient.addColorStop(0.5, this.color);
            gradient.addColorStop(1, 'rgba(255, 255, 255, 0.1)');
            
            ctx.fillStyle = gradient;
            ctx.beginPath();
            ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
            ctx.fill();
            
            // Add highlight
            ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
            ctx.beginPath();
            ctx.arc(this.x - this.size * 0.3, this.y - this.size * 0.3, this.size * 0.3, 0, Math.PI * 2);
            ctx.fill();
            break;

          case 'raindrops':
            // Draw raindrop
            ctx.strokeStyle = this.color;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(this.x, this.y);
            ctx.lineTo(this.x, this.y + this.size * 3);
            ctx.stroke();
            
            // Add raindrop tip
            ctx.fillStyle = this.color;
            ctx.beginPath();
            ctx.arc(this.x, this.y, this.size * 0.5, 0, Math.PI * 2);
            ctx.fill();
            break;

          case 'snow':
            // Draw snowflake
            ctx.fillStyle = this.color;
            ctx.beginPath();
            for (let i = 0; i < 6; i++) {
              ctx.moveTo(this.x, this.y);
              ctx.lineTo(this.x + Math.cos(i * Math.PI / 3) * this.size, 
                        this.y + Math.sin(i * Math.PI / 3) * this.size);
            }
            ctx.stroke();
            
            // Add center dot
            ctx.beginPath();
            ctx.arc(this.x, this.y, this.size * 0.3, 0, Math.PI * 2);
            ctx.fill();
            break;

          case 'floating':
            // Draw floating particle with glow
            const glowGradient = ctx.createRadialGradient(
              this.x, this.y, 0,
              this.x, this.y, this.size * 2
            );
            glowGradient.addColorStop(0, this.color);
            glowGradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
            
            ctx.fillStyle = glowGradient;
            ctx.beginPath();
            ctx.arc(this.x, this.y, this.size * 2, 0, Math.PI * 2);
            ctx.fill();
            
            // Add core
            ctx.fillStyle = this.color;
            ctx.beginPath();
            ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
            ctx.fill();
            break;

          default:
            // Default particle
            ctx.fillStyle = this.color;
            ctx.beginPath();
            ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
            ctx.fill();
        }

        ctx.restore();
      }
    }

    // Create particles
    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle());
    }

    // Animation loop
    const animate = () => {
      // Check if particles are still active
      if (!isActive) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        return;
      }
      
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      particles.forEach(particle => {
        particle.update();
        particle.draw();
      });
      
      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    // Handle resize
    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      // Clear canvas on cleanup
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    };
  }, [theme, isActive]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0"
      style={{ zIndex: 0 }}
    />
  );
};

export default ParticleEffects;
